import React from "react";
import LocalShippingOutlinedIcon from "@material-ui/icons/LocalShippingOutlined";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";

import Badge from "../../../../../../components/common/PL/Badge/Badge";
import Button from "../../../../../../components/common/PL/Button/Button";
import PersonaEstadoStatus from "./PersonaEstadoStatus";
import VerificarControlesEgresoModal from "./VerificarControlesEgresoModal";
import AutorizadoresModal from "./AutorizadoresModal";
import { COMMUNICATION_METHOD_LABELS } from "./RegistrarAutorizacionModal";
import {
  ExitAuthorization,
  GateControlKey,
  GateControlObservation,
} from "./transportAuthorizationTypes";
import { formatDateTimeShort, isWithinRange } from "./transportDateFormat";

const CARD_STATUS_CLASSES: Partial<Record<ExitAuthorization["status"], string>> = {
  pending: "transportEgresoCardPendiente",
  rejected: "transportEgresoCardRechazada",
  approved: "transportEgresoCardAprobada",
};

type AutorizacionEgresoCardProps = {
  autorizacion: ExitAuthorization;
  isSelected: boolean;
  isExpanded: boolean;
  onSelect: (auth: ExitAuthorization) => void;
  onToggleExpand: (authId: string) => void;
  onReenviar: (authId: string) => void;
  /** Ausente en las vistas de sólo lectura (tarjeta del acompañante). */
  onRegistrarPorOtroMedio?: (authId: string) => void;
  onVerificarControl: (authId: string, controlId: GateControlKey) => void;
  observations: Partial<Record<GateControlKey, GateControlObservation>>;
  onObservacionTextChange: (authId: string, controlId: GateControlKey, text: string) => void;
  onObservacionPhotoChange: (authId: string, controlId: GateControlKey, photo: File | null) => void;
  disabled?: boolean;
};

const ControlRow: React.FC<{ label: string; checked: boolean }> = ({ label, checked }) => (
  <div className="transportEgresoControlRow">
    {checked ? (
      <CheckBoxIcon style={{ color: "#2be65e", fontSize: "22px" }} />
    ) : (
      <CheckBoxOutlineBlankIcon style={{ color: "#94A3B8", fontSize: "22px" }} />
    )}
    <span className={checked ? "transportEgresoControlChecked" : ""}>{label}</span>
  </div>
);

const AutorizacionEgresoCard: React.FC<AutorizacionEgresoCardProps> = ({
  autorizacion: auth,
  isSelected,
  isExpanded,
  onSelect,
  onToggleExpand,
  onReenviar,
  onRegistrarPorOtroMedio,
  onVerificarControl,
  observations,
  onObservacionTextChange,
  onObservacionPhotoChange,
  disabled = false,
}) => {
  const [isVerificarModalOpen, setIsVerificarModalOpen] = React.useState(false);
  const [isAutorizadoresModalOpen, setIsAutorizadoresModalOpen] = React.useState(false);

  // Sólo una autorización aprobada y vigente puede usarse: pendientes, rechazadas, utilizadas,
  // vencidas y canceladas se muestran, pero no se pueden seleccionar.
  const isSelectable = auth.status === "approved";
  const vigente = isWithinRange(auth.validFromTs, auth.validToTs, auth.validFrom, auth.validTo);
  const canSelect = isSelectable && vigente;
  const allVerificado = auth.controls.every((c) => c.verified);
  // Pendiente = todavía no hay host real asignado (hostName/hostPhone vendrían de tomar
  // arbitrariamente el primer manager notificado); recién hay host una vez aprobada o rechazada.
  const isPending = auth.status === "pending";
  // Aprobada pero vencida: no es utilizable, así que no la pintamos como si lo fuera (igual que
  // en la lista de entrada).
  const cardStatusClass =
    auth.status === "approved" && !vigente ? "" : CARD_STATUS_CLASSES[auth.status] ?? "";
  // Igual que en las tarjetas de entrada: el badge Vigente/Fuera de vigencia no aplica
  // mientras la autorización está pendiente o fue rechazada.
  const showVigenteBadge = auth.status !== "pending" && auth.status !== "rejected";

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    if (!canSelect) return;
    if (!allVerificado) {
      setIsVerificarModalOpen(true);
      return;
    }
    onSelect(auth);
  };

  const handleVerificarClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setIsVerificarModalOpen(true);
  };

  return (
    <div
      className={`transportAuthCard ${cardStatusClass} ${
        isSelected ? "transportAuthCardSelected" : ""
      }`}
      onClick={() => !disabled && onToggleExpand(auth.id)}
    >
      <div className="transportAuthCardHeaderArea">
        <div className="transportAuthCardTop">
          <div className="transportEgresoHeaderLeft">
            {canSelect && (
              <div className="transportPersonaCheckbox" onClick={handleCheckboxClick}>
                {isSelected ? (
                  <CheckBoxIcon style={{ color: "#2be65e", fontSize: "28px" }} />
                ) : (
                  <CheckBoxOutlineBlankIcon style={{ color: "#003350", fontSize: "28px" }} />
                )}
              </div>
            )}
            <div className="transportAuthCardTitleGroup">
              <div className="transportAuthTitleBlock">
                {isPending ? (
                  <div onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="outline"
                      size="small"
                      onClick={() => setIsAutorizadoresModalOpen(true)}
                    >
                      Ver listado de autorizadores {`(${auth.people.length})`}
                    </Button>
                  </div>
                ) : auth.guardApproval.approvedByGuard ? (
                  // La aprobó el guardia: no hay anfitrión al que atribuirla ni teléfono que dar.
                  <h4 className="transportAuthCardTitle transportEgresoHostName">
                    Autorizado por guardia
                  </h4>
                ) : (
                  <>
                    <h4 className="transportAuthCardTitle transportEgresoHostName">
                      {auth.hostName}
                    </h4>
                    <span className="transportAuthCardSubtitle">Tel: {auth.hostPhone}</span>
                  </>
                )}
              </div>
              <div className="transportPillRow">
                <Badge
                  variant={auth.loaded ? "blue" : "yellow"}
                  label={auth.loaded ? "Cargado" : "Vacío"}
                />
                {!auth.loaded && auth.merchandiseInTransit && (
                  <Badge variant="brown" label="Merc. en tránsito" />
                )}
                <Badge
                  variant={auth.kind === "request" ? "teal" : "purple"}
                  label={auth.kind === "request" ? "Solicitud" : "Preautorización"}
                />
              </div>
            </div>
          </div>
          <div className="transportAuthCardRight">
            <span className="transportPatenteChip">{auth.vehiclePlate}</span>
            <ExpandMoreIcon
              style={{
                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease",
                color: "#64748B",
              }}
            />
          </div>
        </div>

        <div className="transportEgresoCardBottomRow">
          <div className="transportPersonaInfo">
            <div className="transportAuthCardBottom">
              <LocalShippingOutlinedIcon fontSize="small" style={{ color: "#94A3B8" }} />
              <span className="transportAuthCardMetaText">
                Egreso:{" "}
                <span className={!vigente ? "transportVigenciaVencida" : undefined}>
                  {formatDateTimeShort(auth.validFrom)} – {formatDateTimeShort(auth.validTo)} hs
                </span>
                &nbsp;|&nbsp; DNI chofer: {auth.driverDni} ({auth.driverCountry})
              </span>
              {showVigenteBadge && (
                <Badge
                  variant={vigente ? "green" : "red"}
                  label={vigente ? "Vigente" : "Fuera de vigencia"}
                />
              )}
            </div>
            {auth.status === "rejected" && (
              <span className="transportPersonaMotivoRechazo">
                Motivo de rechazo: {auth.rejectionReason || "-"}
              </span>
            )}
          </div>
          <div onClick={(e) => e.stopPropagation()}>
            <PersonaEstadoStatus
              status={auth.status}
              requestedAt={auth.requestedAt}
              isValid={vigente}
              onReenviar={() => onReenviar(auth.id)}
            />
          </div>
        </div>

        {isPending && onRegistrarPorOtroMedio && (
          <div className="transportRegistrarOtroMedioRow" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="outline"
              size="small"
              className="transportRegistrarOtroMedioButton transportRegistrarOtroMedioButtonInline"
              onClick={() => onRegistrarPorOtroMedio(auth.id)}
            >
              Registrar autorización por otro medio
            </Button>
          </div>
        )}
      </div>

      <div className={`transportAuthCardAccordionContent ${isExpanded ? "open" : ""}`}>
        <div className="transportAuthCardAccordionInner">
          <div className="transportEgresoDetailContainer" onClick={(e) => e.stopPropagation()}>
            <div className="transportEgresoDetailGrid">
              <div className="transportEgresoDetailRow">
                <span className="transportEgresoDetailLabel">Patente vehículo (tractor)</span>
                <span className="transportPatenteChip">{auth.vehiclePlate}</span>
              </div>
              <div className="transportEgresoDetailRow">
                <span className="transportEgresoDetailLabel">Patente acoplado</span>
                {auth.trailerPlate ? (
                  <span className="transportPatenteChip">{auth.trailerPlate}</span>
                ) : (
                  <span className="transportEgresoDetailNoAplica">No aplica</span>
                )}
              </div>
              <div className="transportEgresoDetailRow">
                <span className="transportEgresoDetailLabel">DNI chofer</span>
                <span className="transportEgresoDetailValue">
                  {auth.driverDni} · {auth.driverCountry}
                </span>
              </div>
              <div className="transportEgresoDetailRow">
                <span className="transportEgresoDetailLabel">Fecha de egreso</span>
                <span className="transportEgresoDetailValue">
                  {formatDateTimeShort(auth.validFrom)} – {formatDateTimeShort(auth.validTo)} hs
                </span>
              </div>
              {auth.loaded && (
                <>
                  <div className="transportEgresoDetailRow">
                    <span className="transportEgresoDetailLabel">N° de remito</span>
                    <span className="transportEgresoDetailValue">{auth.receipt || "-"}</span>
                  </div>
                  <div className="transportEgresoDetailRow">
                    <span className="transportEgresoDetailLabel">N° de precinto</span>
                    <span className="transportEgresoDetailValue">{auth.seal || "-"}</span>
                  </div>
                </>
              )}
              {/* Sólo cuando el egreso sale con custodio vehicular y se cargaron sus datos. */}
              {auth.vehicleCustodyPlate && (
                <>
                  <div className="transportEgresoDetailRow">
                    <span className="transportEgresoDetailLabel">Patente custodio</span>
                    <span className="transportPatenteChip">{auth.vehicleCustodyPlate}</span>
                  </div>
                  <div className="transportEgresoDetailRow">
                    <span className="transportEgresoDetailLabel">Empresa custodio</span>
                    <span className="transportEgresoDetailValue">
                      {auth.vehicleCustodyCompany || "-"}
                    </span>
                  </div>
                </>
              )}
            </div>

            <div className="transportEgresoControlesSection">
              <span className="transportEgresoControlesTitle">Controles en portería</span>
              {auth.controls.length === 0 ? (
                <span className="transportEgresoControlesNone">
                  No se requieren controles para esta autorización.
                </span>
              ) : (
                <div className="transportEgresoControlesGrid">
                  {auth.controls.map((control) => (
                    <ControlRow key={control.id} label={control.label} checked={control.verified} />
                  ))}
                </div>
              )}
              {canSelect && (
                <Button
                  variant="outline"
                  size="small"
                  onClick={() => handleVerificarClick()}
                  className="transportEgresoVerificarButton"
                >
                  Verificar
                </Button>
              )}
            </div>

            {/* Sólo aparecen cuando la aprobó el guardia por fuera de la app. */}
            {(auth.guardApproval.communicationMethod || auth.guardApproval.authorizedBy) && (
              <div className="transportEgresoDetailGrid">
                {auth.guardApproval.communicationMethod && (
                  <div className="transportEgresoDetailRow">
                    <span className="transportEgresoDetailLabel">Medio de autorización</span>
                    <span className="transportEgresoDetailValue">
                      {COMMUNICATION_METHOD_LABELS[auth.guardApproval.communicationMethod]}
                    </span>
                  </div>
                )}
                {auth.guardApproval.authorizedBy && (
                  <div className="transportEgresoDetailRow">
                    <span className="transportEgresoDetailLabel">Responsable que autorizó</span>
                    <span className="transportEgresoDetailValue">
                      {auth.guardApproval.authorizedBy}
                    </span>
                  </div>
                )}
              </div>
            )}

            {auth.gateObservations && (
              <div className="transportObsBox">
                <span className="transportObsBoxLabel">
                  {auth.kind === "request"
                    ? "Observaciones para autorizador"
                    : "Observaciones para portería"}
                </span>
                <span className="transportObsBoxText">{auth.gateObservations}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <VerificarControlesEgresoModal
        open={isVerificarModalOpen}
        onClose={() => setIsVerificarModalOpen(false)}
        controles={auth.controls}
        vigente={vigente}
        onVerificar={(controlId) => onVerificarControl(auth.id, controlId)}
        observations={observations}
        onObservacionTextChange={(controlId, text) =>
          onObservacionTextChange(auth.id, controlId, text)
        }
        onObservacionPhotoChange={(controlId, photo) =>
          onObservacionPhotoChange(auth.id, controlId, photo)
        }
        onSeleccionar={() => {
          setIsVerificarModalOpen(false);
          // El botón del modal confirma la selección; nunca la deshace (deseleccionar es sólo
          // del checkbox de la tarjeta). Sin este guard, confirmar sobre una autorización ya
          // seleccionada la deseleccionaba y descartaba las observaciones recién cargadas.
          if (!isSelected) onSelect(auth);
        }}
      />

      <AutorizadoresModal
        open={isAutorizadoresModalOpen}
        onClose={() => setIsAutorizadoresModalOpen(false)}
        people={auth.people}
      />
    </div>
  );
};

export default AutorizacionEgresoCard;
